/**
 * control-drawer.js
 */

import React from 'react';
import {
  useTheme,
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import analytics from 'utils/analytics';
import Icon from 'components/icon';
import { TRACK_TYPE, TRACKING_STATE } from '../tracking-common';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { TRACK_VISIBILITY, TrackingFormContext } from '../live-tracking/tracking-form-context';
import { MapContext } from '../map/map-context';
import { TrackingDrawerContext } from './drawer-context';
import { 
  DrawerHeader,
  DrawerControlButton,
  DrawerControlBar,
  DrawerBase, 
} from './control-common';
import { 
  DRAWER_MENU_ITEM,
  DrawerContextMenu,
} from './drawer-context-menu';
import ContentUnstarted from './content-unstarted';
import ContentActive from './content-active';
import ContentStopped from './content-stopped';


const Header = (props) => {
  const {
    title,
    startIconId,
    endIconId,
  } = props;
  const theme = useTheme();
  const { trackingState } = React.useContext(LiveTrackingContext);
  const { isDrawerExpanded } = React.useContext(TrackingDrawerContext);
  const chipIconName = {
    [TRACKING_STATE.UNSTARTED]: 'location-off',
    [TRACKING_STATE.ACTIVE]: 'location-on',
    [TRACKING_STATE.STOPPED]: 'tracking-paused'
  }[trackingState];
  const chipLabel = {
    [TRACKING_STATE.UNSTARTED]: 'Not Started',
    [TRACKING_STATE.ACTIVE]: 'Active',
    [TRACKING_STATE.STOPPED]: 'Paused',
  }[trackingState];
  const chipClass = {
    [TRACKING_STATE.UNSTARTED]: 'chip-incomplete chip-font-incomplete',
    [TRACKING_STATE.ACTIVE]: 'chip-complete chip-font-complete',
    [TRACKING_STATE.STOPPED]: 'chip-warning animate-flash',
  }[trackingState];
  const titleColor = theme.palette.sys.onPrimary;


  return (
    <DrawerHeader backgroundColor={theme.palette.sys.primary}>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, px: 2, }}>
        <Box className='flex-hc' sx={{ flex: 1 }}>
          {startIconId && <Icon type={startIconId} sx={{ mr: 1, color: titleColor }} />}
          <Typography variant='subtitle1' sx={{ color: titleColor }}>{title}</Typography>
          {endIconId && <Icon type={endIconId} sx={{ ml: 1 }} />}
        </Box>
        <Chip 
          size='small' 
          icon={<Icon type={chipIconName} style={{ color: 'inherit' }} />}
          label={chipLabel} 
          className={chipClass ?? ''} 
          sx={{ px: 1, mr: 1.5, }}
        />
        <Icon 
          type='collapse' 
          sx={{ 
            color: titleColor,
            transition: (theme) => `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
            transform: `rotateZ(${isDrawerExpanded ? 180 : 0}deg)`
          }} 
        />
      </Box>
    </DrawerHeader>
  );
}


const StartButton = () => {
  const { startTracking } = React.useContext(LiveTrackingContext);
  const { trackType, dog, trackName, followExercise, followLeadTrack, trackVisibility } = React.useContext(TrackingFormContext);
  const { mapType } = React.useContext(MapContext);
  const visibilityValue = trackVisibility?.[followLeadTrack?.id] ?? TRACK_VISIBILITY.HIDDEN;

  if (trackType !== TRACK_TYPE.LEAD && !dog) {
    return "A valid dog must be selected";
  }

  return (
    <DrawerControlButton
      color='secondary'
      iconName='tracking-start'
      disabled={Boolean(!window.ReactNativeWebView)}
      onClick={() => {
        // build track-creation params for type
        const params = {};
        if (trackType === TRACK_TYPE.DEPLOYMENT) {
          params.dogId = dog.id;
        } else if (trackType === TRACK_TYPE.LEAD) {
          params.name = trackName;
        } else if (trackType === TRACK_TYPE.FOLLOW) {
          params.leadTrackId = followLeadTrack.id;
          params.exerciseId = followExercise.id;
          params.dogId = dog.id;
          params.isBlind = visibilityValue !== TRACK_VISIBILITY.VISIBLE;
        }        
        
        analytics.trackGA4('event', { name: 'track_start', params: { 
          trackstart_rec_type: trackType,
          trackstart_auto_create_exercise: trackType === TRACK_TYPE.FOLLOW 
            ? followExercise.id === null ? 'yes': 'no'
            : 'N/A',
          trackstart_has_laid_track: trackType === TRACK_TYPE.FOLLOW 
            ? followLeadTrack.id !== null ? 'yes' : 'no'
            : 'N/A',
          trackstart_laid_track_visibility: (trackType === TRACK_TYPE.FOLLOW && followLeadTrack.id !== null)
            ? visibilityValue
            : 'N/A',
          trackstart_map_tileset: mapType,
        }});

        startTracking(trackType, params, trackType === TRACK_TYPE.FOLLOW ? followLeadTrack : null);
      }}
    >
      <Typography 
        variant='subtitle2' 
        sx={{ fontWeight: 'bold', fontSize: 'sizes.3' }}
      >
        Start Track
      </Typography>
    </DrawerControlButton>
  );
}


const StopButton = () => {
  const { stopTracking, saveActiveTrackStats } = React.useContext(LiveTrackingContext);
  const { setIsDrawerExpanded } = React.useContext(TrackingDrawerContext);

  return (
    <DrawerControlButton
      color='secondary'
      iconName='tracking-stop'
      onClick={async () => {
        stopTracking();
        saveActiveTrackStats();
        setIsDrawerExpanded(true);
      }}
    >
      <Typography 
        variant='subtitle2' 
        sx={{ fontSize: 'sizes.2', lineHeight: 1.1, textAlign: 'left' }}
      >
        Stop<br />Track
      </Typography>
    </DrawerControlButton>
  )
}


const ResumeButton = () => {
  const { trackType } = React.useContext(TrackingFormContext);
  const { resumeTracking } = React.useContext(LiveTrackingContext);

  return (
    <Button
      variant='text'
      startIcon={<Icon type='nav-back' />}
      sx={{
        textTransform: 'none',
        whiteSpace: 'nowrap',
        color: 'primary.dark',
      }}
      onClick={() => {
        analytics.trackGA4('event', { name: 'track_stop', params: { trackstop_rec_type: trackType, trackstop_action: 'resume' } });
        resumeTracking()
      }}
    >
      RESUME current track
    </Button>
  )
}


const QuickMarkerButton = () => {
  const { currentLocation } = React.useContext(LiveTrackingContext);
  const { addMarker } = React.useContext(MapContext);

  const { getInputProps, open: openDropzone } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: 'image/jpeg',
    onDropAccepted: (files) => {
      addMarker(currentLocation.latitude, currentLocation.longitude, files[0]);
    },
  });

  return (
    <Box sx={{ mx: 1, display: 'flex' }}>
      <DrawerControlButton
        color='info'
        sx={{ mr: 0.5 }}
        onClick={() => addMarker(currentLocation.latitude, currentLocation.longitude)}
      >
        <Icon type='location-add' />
      </DrawerControlButton>
      <Box sx={{ display: 'none' }}>
        <input {...getInputProps()} capture='environment' />
      </Box>
      <DrawerControlButton
        color='info'
        sx={{ ml: 0 }}
        onClick={() => openDropzone()}
      >
        <Icon type='camera-marker' />
      </DrawerControlButton>
    </Box>
  )
}


const ControlsUnstarted = (props) => {
  return (
    <DrawerControlBar
      actions={<StartButton />}
      menu={<DrawerContextMenu 
        items={[
          // Temporarily disable...
          //DRAWER_MENU_ITEM.SHARE_LOCATION,
          //{ id: 'divider' }, 
          DRAWER_MENU_ITEM.HELP,
        ]}
      />}
    />
  );
}


const ControlsActive = (props) => {
  return (
    <DrawerControlBar
      actions={<>
        <StopButton />
        <QuickMarkerButton />
      </>}
      menu={<DrawerContextMenu 
        items={[
          // Temporarily disable...
          //DRAWER_MENU_ITEM.SHARE_LOCATION,
          //{ id: 'divider' }, 
          DRAWER_MENU_ITEM.HELP,
        ]}
      />}
    />
  );
}


const ControlsStopped = (props) => {
  return (
    <DrawerControlBar
      leftActions={<ResumeButton />}
    />
  );
}


const HEADER_TITLE = {
  [TRACKING_STATE.UNSTARTED]: {
    title: 'TRACKING'
  },
  [TRACK_TYPE.DEPLOYMENT]: {
    title: 'DEPLOYMENT',
    startIconId: 'track-deployment'
  },
  [TRACK_TYPE.LEAD]: {
    title: 'LAY TRACK',
    startIconId: 'track-lead'
  },
  [TRACK_TYPE.FOLLOW]: {
    title: 'FOLLOW',
    startIconId: 'track-follow'
  }
};


const CONTENT = {
  [TRACKING_STATE.UNSTARTED]: <ContentUnstarted />,
  [TRACKING_STATE.ACTIVE]: <ContentActive />,
  [TRACKING_STATE.STOPPED]: <ContentStopped />,
};


const CONTROLS = {
  [TRACKING_STATE.UNSTARTED]: <ControlsUnstarted />,
  [TRACKING_STATE.ACTIVE]: <ControlsActive />,
  [TRACKING_STATE.STOPPED]: <ControlsStopped />
}


export default function ControlDrawer(props) {
  const { trackingState } = React.useContext(LiveTrackingContext);
  const { trackType } = React.useContext(TrackingFormContext);
  const titleParts = trackingState === TRACKING_STATE.UNSTARTED
    ? HEADER_TITLE[trackingState]
    : HEADER_TITLE[trackType];

  return (
    <DrawerBase
      {...props}
      header={<Header title={titleParts?.title} startIconId={titleParts?.startIconId} endIconId={titleParts?.endIconId} />}
      content={CONTENT[trackingState]}
      controls={CONTROLS[trackingState]}
      keepControlsVisible={trackingState === TRACKING_STATE.ACTIVE}
    />
  );
}